import { useEffect, useRef, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { List as VirtualList, type RowComponentProps } from 'react-window';
import * as exifr from 'exifr';
import { uploadLinkApi, uploadToSignedUrl, type UploadLinkOrder, type UploadLinkSession, type Cover } from '@/lib/api';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import { isIosDevice, galleryAccept } from '@/lib/iosPicker';
import { detectDevice } from '@/lib/detectDevice';
import { classifyUploadSelection, uploadNoticeFor } from '@/lib/uploadFileFilter';
import { photosApi } from '@/lib/api';
import { confirmOriginalLanded } from '@/lib/originalsGate';
import { putOrCleanupOrphan } from '@/lib/uploadOrphanCleanup';
import { uploadViaTus } from '@/lib/tusUpload';
import { normalizeJpegOrientation } from '@/lib/normalizeOrientation';
import { sha256Hex } from '@/lib/contentHash';
import { makeThumbnail } from '@/lib/makeThumbnail';
import { PRINT_MAX_DIM, PRINT_JPEG_QUALITY, DISPLAY_MAX_DIM, DISPLAY_JPEG_QUALITY, TILE_MAX_DIM, TILE_JPEG_QUALITY } from '@/lib/imageLimits';
import { dateFromFilename } from '@/lib/filenameDate';
import { IosUploadNote } from '@/components/IosUploadNote';
import { UploadedTileGrid } from '@/components/upload/UploadedTileGrid';
import { HelpFAB } from '@/components/HelpFAB';

// HEIC strategy: we DON'T convert client-side. heic2any uses an old libheif
// build that rejects iOS 17+ HEVC variants with "Could not parse HEIF file"
// errors that flood the console. Instead:
//   - Preview: extract the embedded JPEG thumbnail via exifr (~50ms)
//   - Upload: send the raw .heic file to Storage as-is
//   - Server: web/src/lib/pdfGenerator.ts handles HEIC during PDF generation
// This is faster (no WASM decode), produces no console errors, and works
// uniformly across browsers + iOS versions.

// ─────────────────────────────────────────────────────────────────────────────
// MagicUploadPage — UPLOAD-AS-PICKED, memory-bounded flow.
//
// WHY (iOS "select-all → everything disappears" bug):
//   The old "stage then submit" flow held EVERY picked File in memory (twice:
//   `file` + `originalFile`) plus a decoded preview blob each, with ALL tiles
//   mounted unvirtualized, and only started uploading on a manual "Envoyer"
//   tap. For a 100+ photo iPhone album that blew past iOS Safari's per-tab RAM
//   → iOS discarded + reloaded the tab → `files=[]` BEFORE the upload ever
//   started → nothing uploaded. The fix is to BOUND MEMORY so the reload never
//   happens.
//
// Key UX decisions NOW:
//   1. Files upload AS PICKED. `addFiles` immediately feeds the bounded pump
//      (UPLOAD_PARALLEL=10). No "Envoyer" gate. Photos added mid-upload just
//      join the in-flight queue (the pump is re-entrant via a running ref).
//   2. The MOMENT a file finishes (or fails for good), ALL its heavy bytes are
//      released: `file`/`originalFile`/`prepared` → null + revokeObjectURL on
//      its preview blob. Only a light record survives ({id, photo_id, status,
//      thumbUrl?}). At most ~10 File objects are ever held → RAM stays flat.
//   3. The tile grid is VIRTUALIZED (react-window) — only on-screen tiles
//      mount, so the DOM stays tiny regardless of photo count.
//   4. HEIC: skip libheif; iOS Safari decodes HEIC natively via canvas, others
//      fall back to raw HEIC upload (PDF generator handles it server-side).
//   5. The big sticky button is now "Continuer" — a PROCEED action, enabled
//      once ≥ MIN_PHOTOS are UPLOADED and nothing is still in-flight. It no
//      longer starts the upload (that already ran on pick). Confetti + the
//      "add more" / album-reselect behavior are preserved.
// ─────────────────────────────────────────────────────────────────────────────

// status lifecycle: uploading → done | error. (No more 'staged' — uploads start
// on pick. 'queued' = accepted, waiting for a free pump slot.)
type Status = 'queued' | 'uploading' | 'done' | 'error';

interface FileItem {
  id: string;
  // Heavy bytes — held ONLY until this file finishes, then nulled to free RAM.
  // `file`/`originalFile` are the source bytes; `prepared` caches the
  // compressed/converted upload bytes so a retry doesn't re-prep.
  file: File | null;            // current file to upload (may be converted/compressed)
  originalFile: File | null;    // kept for HEIC re-attempts and EXIF
  prepared: File | null;        // last prepFile() result, for retry without re-prep
  // Light display fields — survive after release.
  previewUrl: string | null;    // local blob preview (revoked on finish)
  thumbUrl: string | null;      // server thumbnail signed URL (cheap, survives)
  photoId: string | null;       // server photo id (for retry + light record)
  status: Status;
  progress: number;
  error?: string;
  duplicate?: boolean;          // worker rejected it as a re-upload (FIX 1)
  released?: boolean;           // heavy bytes dropped → retry must re-pick
}

const MIN_PHOTOS = 20;          // proceed gate: this many photos must be UPLOADED before "Continuer"

// Magic-upload palette — already on canonical gold; align the rest of the
// tokens to lib/brand.ts so all customer surfaces share one cream/text
// scale.
const BRAND = {
  gold:      '#C9A24A',  // BRAND.gold
  goldDeep:  '#A8842F',  // BRAND.goldDeep
  cream:     '#F5EFE0',  // BRAND.beige
  creamSoft: '#FAF6EC',  // BRAND.beigeLight
  ink:       '#1F2937',  // BRAND.dark
  muted:     '#6B7280',  // BRAND.muted
  border:    '#E5E0D2',  // BRAND.border
  success:   '#10B981',  // BRAND.success
};

const UPLOAD_PARALLEL = 10;      // was 6 — Supabase Storage handles this fine, monitor for rate-limit errors and drop back if needed
// FIX #2 (mirrored from AlbumFlowPage) — mobile upload concurrency. 10 parallel
// 3600px PUTs starve a Samsung/Android uplink → the batch crawls. On mobile fire
// fewer-but-faster PUTs (4); desktop keeps the full 10.
const isThrottledUpload = (): boolean => {
  // ONLY Android needs the lower width (weak uplink); iPhone/iPad upload fast at 10.
  const d = detectDevice();
  if (d === 'Android') return true;
  if (d === 'Mobile') {
    try {
      const uaDataMobile = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile;
      if (uaDataMobile) return true;
    } catch { /* noop */ }
  }
  return false;
};
const UPLOAD_PARALLEL_MOBILE = 4;
const UPLOAD_PARALLEL_EFFECTIVE = isThrottledUpload() ? UPLOAD_PARALLEL_MOBILE : UPLOAD_PARALLEL;
const HEIC_CONCURRENT = 2;       // libheif WASM is single-threaded — keep low
const TARGET_DIM = PRINT_MAX_DIM;       // see lib/imageLimits.ts (aspect always preserved)
const TARGET_QUALITY = PRINT_JPEG_QUALITY;

// ── Stall backstops (freeze fix — same as AlbumFlowPage) ──────────────────────
// A single hung file used to hold its pump slot forever (img.decode() of a HEIC
// variant that never settles, a flaky-cellular signed-URL mint, …). After ~10
// hangs activeRef pinned at UPLOAD_PARALLEL and the whole queue froze. These
// bounds GUARANTEE every step settles so the slot ALWAYS frees and the pump
// keeps draining. Durations are generous so legit slow-cellular files finish.
const PER_FILE_TIMEOUT_MS = 90_000; // hard ceiling on a whole processOne
const HEIC_DECODE_TIMEOUT_MS = 20_000; // img.decode() can hang on some HEIC variants
const HASH_TIMEOUT_MS = 10_000;     // sha256Hex of a big blob
const UPLOAD_URL_TIMEOUT_MS = 20_000; // photoUploadUrl fetch on flaky cellular
const UPLOAD_MAX_RETRIES = 2;       // retry a hung/5xx/network file up to 2x
const UPLOAD_RETRY_BACKOFF_MS = [1_000, 3_000]; // backoff before retry 1, retry 2

/** Marks a hard 4xx so the retry loop won't retry it (dedup / bad request). */
class NonRetryableUploadError extends Error {
  constructor(message: string) { super(message); this.name = 'NonRetryableUploadError'; }
}

/** Race `work` against a timeout that REJECTS so the caller's catch/finally
 *  still runs (slot frees, permits release). Work keeps running but holds nothing. */
function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const e = new Error(`Timeout après ${ms}ms: ${label}`);
      e.name = 'TimeoutError';
      reject(e);
    }, ms);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

// ─── HEIC pipeline ──────────────────────────────────────────────────────────

class Semaphore {
  private slots: number;
  private queue: (() => void)[] = [];
  constructor(limit: number) { this.slots = limit; }
  acquire(): Promise<void> {
    if (this.slots > 0) { this.slots--; return Promise.resolve(); }
    return new Promise<void>((r) => this.queue.push(r));
  }
  release() {
    if (this.queue.length > 0) this.queue.shift()!();
    else this.slots++;
  }
}
function isHeicFile(file: File): boolean {
  if (file.type === 'image/heic' || file.type === 'image/heif') return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

function resolveContentType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'heic': return 'image/heic';
    case 'heif': return 'image/heif';
    case 'jpg': case 'jpeg': return 'image/jpeg';
    case 'png': return 'image/png';
    case 'webp': return 'image/webp';
    default: return 'image/jpeg';
  }
}

// Pull the embedded JPEG thumbnail from a HEIC file — instant preview without
// touching libheif. iPhones bake one in by default. Wrapped in a semaphore so
// 25 simultaneous extractions don't choke the main thread on slow phones.
const previewSem = new Semaphore(HEIC_CONCURRENT);
async function extractHeicThumbnail(file: File): Promise<string | null> {
  await previewSem.acquire();
  try {
    const thumb = (await exifr.thumbnail(file)) as Uint8Array | Buffer | undefined | null;
    if (thumb && thumb.byteLength > 100) {
      return URL.createObjectURL(new Blob([thumb as BlobPart], { type: 'image/jpeg' }));
    }
  } catch { /* silent */ }
  finally { previewSem.release(); }
  return null;
}

// Resize + compress a JPEG/PNG to TARGET_DIM long side at TARGET_QUALITY.
// Skipped for HEIC (we upload raw — see top-of-file note).
async function compressImage(file: File): Promise<File> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      if (Math.max(w, h) <= TARGET_DIM && file.size < 800_000) { resolve(file); return; }
      const scale = Math.min(1, TARGET_DIM / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) { resolve(file); return; }
          const baseName = file.name.replace(/\.(jpe?g|png|webp)$/i, '');
          resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified }));
        },
        'image/jpeg',
        TARGET_QUALITY,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// Native browser HEIC → JPEG conversion. iOS Safari renders HEIC in <img>
// natively, so once it's drawn to canvas we can re-encode as JPEG. This
// works for ALL iPhone HEIC variants (no libheif WASM involved).
//
// On browsers without native HEIC support (Chrome/Firefox on Mac/Android),
// img.decode() rejects and we fall back to uploading the raw HEIC.
async function heicToJpegViaCanvas(file: File): Promise<File | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    // decode() throws if the browser can't decode the format — but on SOME HEIC
    // variants it neither resolves nor rejects (hangs forever, holding the pump
    // slot). BOUND it: on timeout we throw → return null → fall back to raw HEIC.
    await withTimeout(img.decode(), HEIC_DECODE_TIMEOUT_MS, 'heic decode');
    if (!img.naturalWidth || !img.naturalHeight) throw new Error('zero dimensions');

    // Resize to TARGET_DIM long side so we don't push 12-48 MP iPhone shots
    // straight to Storage.
    const scale = Math.min(1, TARGET_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas ctx unavailable');
    ctx.drawImage(img, 0, 0, w, h);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', TARGET_QUALITY),
    );
    if (!blob) throw new Error('toBlob returned null');

    const baseName = file.name.replace(/\.(heic|heif)$/i, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return null; // browser can't decode HEIC natively — caller falls back
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Best-effort prep: HEIC → try native browser canvas decode (iOS Safari) →
// fall back to raw HEIC upload. Others → compressed JPEG.
async function prepFile(file: File): Promise<File> {
  if (isHeicFile(file)) {
    const jpeg = await heicToJpegViaCanvas(file);
    if (jpeg) return jpeg;          // converted to JPEG — small + universal
    return file;                    // raw HEIC — server-side fallback
  }
  // Bake EXIF orientation into the pixels so the editor + PDF don't show
  // sideways frames for iPhone JPEGs with Orientation=6/8 tags.
  const upright = await normalizeJpegOrientation(file);
  try { return await compressImage(upright); }
  catch { return upright; }
}

// ─── Per-photo metadata extraction (EXIF + dimensions + preview blob) ──────
// Runs once per file before upload. Everything here is best-effort and
// returns sensible nulls on failure — never blocks the upload pipeline.

interface PhotoMeta {
  exifDate: string | null;
  gpsLat: number | null;
  gpsLng: number | null;
  width: number | null;
  height: number | null;
  /** JPEG preview blob for HEIC originals; null for already-renderable formats. */
  previewBlob: Blob | null;
}

async function extractAllMeta(file: File): Promise<PhotoMeta> {
  const result: PhotoMeta = {
    exifDate: null, gpsLat: null, gpsLng: null,
    width: null, height: null, previewBlob: null,
  };

  // EXIF — works for HEIC, JPEG, and most camera formats.
  try {
    const tags = (await exifr.parse(file, { tiff: true, gps: true, exif: true })) as
      | (Record<string, unknown> & {
          DateTimeOriginal?: Date | string;
          CreateDate?: Date | string;
          latitude?: number;
          longitude?: number;
          ImageWidth?: number;
          ImageHeight?: number;
          PixelXDimension?: number;
          PixelYDimension?: number;
          ExifImageWidth?: number;
          ExifImageHeight?: number;
        })
      | null
      | undefined;
    if (tags) {
      const date = tags.DateTimeOriginal ?? tags.CreateDate;
      if (date) {
        result.exifDate = date instanceof Date ? date.toISOString() : new Date(date).toISOString();
      }
      if (typeof tags.latitude === 'number')  result.gpsLat = tags.latitude;
      if (typeof tags.longitude === 'number') result.gpsLng = tags.longitude;
      result.width =
        (typeof tags.ExifImageWidth  === 'number' ? tags.ExifImageWidth  : null) ??
        (typeof tags.PixelXDimension === 'number' ? tags.PixelXDimension : null) ??
        (typeof tags.ImageWidth      === 'number' ? tags.ImageWidth      : null) ?? null;
      result.height =
        (typeof tags.ExifImageHeight === 'number' ? tags.ExifImageHeight : null) ??
        (typeof tags.PixelYDimension === 'number' ? tags.PixelYDimension : null) ??
        (typeof tags.ImageHeight     === 'number' ? tags.ImageHeight     : null) ?? null;
    }
  } catch { /* no EXIF — fine, just leave nulls */ }

  // WhatsApp / Google-Photos / screenshot exports strip EXIF but keep the
  // capture date in the filename (IMG-YYYYMMDD-WAxxxx, 20260515_220826, …).
  // Recover it so chronological album ordering works for these uploads — the
  // worker AI layout sorts by exif_date, so without this magic-link photos get
  // exif_date=NULL and the album order is arbitrary (order #1068). See
  // lib/filenameDate.ts (pure + unit-tested).
  if (!result.exifDate) {
    result.exifDate = dateFromFilename(file.name);
  }

  // For non-HEIC: if we still don't have dimensions, ask the browser via Image.
  if (!isHeicFile(file) && (!result.width || !result.height)) {
    try {
      const dims = await readImageDimensions(file);
      result.width = dims.width;
      result.height = dims.height;
    } catch { /* nope */ }
  }

  // For HEIC: extract the embedded JPEG thumbnail to use as preview. This is
  // the same technique we use for the on-screen tile preview, but here we
  // capture the bytes for upload too.
  if (isHeicFile(file)) {
    try {
      const thumb = (await exifr.thumbnail(file)) as Uint8Array | Buffer | undefined | null;
      if (thumb && thumb.byteLength > 100) {
        result.previewBlob = new Blob([thumb as BlobPart], { type: 'image/jpeg' });
      }
    } catch { /* no embedded thumbnail — admin will get HEIC fallback */ }
  }

  return result;
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function MagicUploadPage() {
  const { token = '' } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [order, setOrder] = useState<UploadLinkOrder | null>(null);
  const [sessions, setSessions] = useState<UploadLinkSession[]>([]);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  // Multi-album (Duo/Trio): the client first picks WHICH album to add photos
  // to. Single-album (Solo) skips this entirely. `selectedAlbumIndex` holds the
  // album_index of the chosen session; null means "show the selection screen".
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState<number | null>(null);
  const isSingleAlbum = sessions.length <= 1;
  const selectedSession: UploadLinkSession | null = isSingleAlbum
    ? (sessions[0] ?? null)
    : (sessions.find((s) => s.album_index === selectedAlbumIndex) ?? null);
  const selectedSessionRef = useRef<UploadLinkSession | null>(null);
  useEffect(() => { selectedSessionRef.current = selectedSession; }, [selectedSession]);

  // ── Album title inline edit (shared by the upload-view header AND the
  //    album-selection cards). One editor open at a time (editingSessionId);
  //    save is optimistic — the session's destination updates immediately and
  //    reverts (+ small red message) if the worker call fails. ──
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<{ sessionId: string; message: string } | null>(null);
  const startTitleEdit = useCallback((sessionId: string) => {
    setTitleError(null);
    setEditingSessionId(sessionId);
  }, []);
  const cancelTitleEdit = useCallback(() => setEditingSessionId(null), []);
  const saveAlbumTitle = useCallback((sessionId: string, value: string) => {
    const prev = sessions.find((s) => s.id === sessionId)?.destination ?? null;
    // Optimistic: show the new title right away, close the editor.
    setSessions((cur) => cur.map((s) => (s.id === sessionId ? { ...s, destination: value } : s)));
    setEditingSessionId(null);
    setTitleError(null);
    uploadLinkApi.setAlbumTitle(token, sessionId, value).catch(() => {
      setSessions((cur) => cur.map((s) => (s.id === sessionId ? { ...s, destination: prev } : s)));
      setTitleError({ sessionId, message: 'Impossible d’enregistrer le titre — réessayez.' });
    });
  }, [sessions, token]);

  const [files, setFiles] = useState<FileItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // iOS picker-open feedback. A web <input type=file> has NO event for the
  // moment the customer taps the iOS picker's blue "Add/Validate" button —
  // iOS withholds `change` until it has finished downloading every selected
  // photo from iCloud (and any forced HEIC transcode). For a big multi-select
  // that's many seconds, during which the page looks frozen. We arm a
  // "Chargement…" overlay BEFORE opening the picker; cleared on `change`,
  // the native `cancel` event, or a safety timeout. iOS-only.
  const [picking, setPicking] = useState(false);
  // Picker-open overlay safety — see PhotoUploadPage for the rationale. The
  // native picker fires no event on cancel, so independent nets (cancel event,
  // focus/visibility return, hard timeout) tear the overlay down. pickerArmedRef
  // is true from open until the picker resolves.
  const PICKER_SAFETY_MS = 12000;
  const pickerArmedRef = useRef(false);
  // FIX #3 — true once a NON-EMPTY `change` lands; lets the focus/visibility net
  // avoid cancelling a Samsung pick that's still resolving (empty change first).
  const sawNonEmptyChangeRef = useRef(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPicking = useCallback(() => {
    pickerArmedRef.current = false;
    if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }
    setPicking(false);
  }, []);

  // Proceed lifecycle: idle (picking / uploading) → done (customer tapped
  // "Continuer" after the minimum was uploaded). Uploads run continuously in
  // the background regardless of this state — it only gates the success screen.
  type SubmitState = 'idle' | 'done';
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const submitStateRef = useRef<SubmitState>('idle');
  useEffect(() => { submitStateRef.current = submitState; }, [submitState]);

  const filesRef = useRef<FileItem[]>([]);
  useEffect(() => { filesRef.current = files; }, [files]);

  // ── Upload pump plumbing (upload-as-picked) ───────────────────────────────
  // queueRef = ids waiting for a free pump slot. runningRef = re-entrancy guard
  // so adding more photos while the pump runs can't double-pump or drop items.
  // activeRef = count of in-flight uploads (source of truth for "all done").
  // One submission batch id per page session: every photo this customer sends
  // from this link gets the same id so admin groups them as one batch.
  const queueRef = useRef<string[]>([]);
  const runningRef = useRef(false);
  const activeRef = useRef(0);
  const submissionBatchIdRef = useRef<string>(crypto.randomUUID());
  // Stable refs so the pump closure (created once) always reads current values.
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);
  const sessionsRef = useRef<UploadLinkSession[]>(sessions);
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);
  // pumpRef holds the (stable) pump fn so addFiles/retry can trigger it without
  // a dependency cycle.
  const pumpRef = useRef<(() => void) | null>(null);

  // Transient feedback when a selection can't be staged (iOS empty FileList,
  // zero-byte iCloud placeholders, or non-images) — replaces the old silent
  // `return` that left the customer staring at an unchanged screen. See
  // lib/uploadFileFilter.
  const [addNotice, setAddNotice] = useState<string | null>(null);
  const addNoticeTimer = useRef<number | undefined>(undefined);
  const flashAddNotice = useCallback((msg: string) => {
    setAddNotice(msg);
    window.clearTimeout(addNoticeTimer.current);
    addNoticeTimer.current = window.setTimeout(() => setAddNotice(null), 8000);
  }, []);

  // Review gallery: bumped after a successful upload batch so the
  // <UploadedGallery> below refetches and shows the newly-uploaded photos.
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);

  // ── Resolve token ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await uploadLinkApi.resolve(token);
        if (cancelled) return;
        setOrder(data.order);
        setSessions(data.sessions);
        setExpiresAt(data.expires_at);
      } catch (err) {
        if (!cancelled) setLinkError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Re-fetch sessions (cheap, public) to refresh per-album photo_count after an
  // upload batch completes, so the selection cards show the new totals. Order +
  // expiry don't change between calls; we only need the refreshed sessions.
  const refreshSessions = useCallback(async () => {
    try {
      const data = await uploadLinkApi.resolve(token);
      setSessions(data.sessions);
    } catch { /* keep stale counts — not worth surfacing an error here */ }
  }, [token]);

  // ── Add files (UPLOAD-AS-PICKED — immediately feeds the pump) ─────────────
  const addFiles = useCallback((incoming: FileList | File[]) => {
    // Don't accept new picks after the customer has tapped "Continuer".
    if (submitStateRef.current === 'done') return;
    // Classify the raw selection so we can surface why nothing was accepted
    // (empty FileList / iCloud not-ready / non-image) instead of silently
    // dropping. See lib/uploadFileFilter.
    const verdict = classifyUploadSelection(incoming);
    const notice = uploadNoticeFor(verdict);
    if (!notice.proceed) { flashAddNotice(notice.blocked ?? ''); return; }
    if (notice.partial) flashAddNotice(notice.partial);
    const arr = verdict.accepted;

    const additions: FileItem[] = arr.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      originalFile: file,
      prepared: null,
      previewUrl: isHeicFile(file) ? null : URL.createObjectURL(file),
      thumbUrl: null,
      photoId: null,
      status: 'queued',
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...additions]);
    // CRITICAL: mirror the additions into filesRef SYNCHRONOUSLY. The pump runs
    // synchronously right below and validates each queued id against
    // filesRef.current — the useEffect that syncs it from `files` only fires
    // AFTER React commits, so without this line every freshly-picked id failed
    // the lookup and was silently dropped from the queue (photos stuck
    // "en attente", zero network requests).
    filesRef.current = [...filesRef.current, ...additions];

    // Enqueue every new file and (re)start the pump. The pump is idempotent
    // (runningRef guard) so calling it again mid-upload just tops up free slots
    // — it never double-pumps or drops items. Files added while uploading just
    // join the in-flight queue here.
    queueRef.current.push(...additions.map((a) => a.id));
    pumpRef.current?.();

    // PERSISTENT small thumbnail per file (~320px, ~15-30KB) that SURVIVES the
    // post-upload RAM release. This is what keeps the customer's photo visible
    // (with just a corner ✓) after it finishes — instead of a blank green tile.
    // Tiny, so RAM stays flat even at 300 photos. HEIC can't canvas-decode
    // everywhere → use its embedded JPEG; others → off-thread OffscreenCanvas.
    additions.forEach((item) => {
      if (!item.file) return;
      const src = item.file;
      const thumbPromise = isHeicFile(src)
        ? extractHeicThumbnail(src)
        : makeThumbnail(src, 320, 0.7).then((b) => (b ? URL.createObjectURL(b) : null));
      thumbPromise.then((url) => {
        if (!url) return;
        setFiles((prev) => prev.map((f) => (f.id === item.id && !f.thumbUrl ? { ...f, thumbUrl: url } : f)));
        filesRef.current = filesRef.current.map((f) => (f.id === item.id && !f.thumbUrl ? { ...f, thumbUrl: url } : f));
      });
    });
  }, [flashAddNotice]);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    // FIX #3b — an EMPTY `change` is NOT a resolution on Samsung: the populated
    // `change` follows a beat later. So only disarm/clear when files actually
    // arrived; on an empty change do nothing (stay armed, keep the value intact)
    // and let the follow-up change or a real cancel resolve it. Value is reset on
    // OPEN now (see the dropzone onClick), not here.
    if (e.target.files?.length) {
      // A real `change` with files → picker resolved: disarm the cancel fallbacks
      // and drop the picker-open overlay. `addFiles` shows per-photo progress.
      sawNonEmptyChangeRef.current = true;
      clearPicking();
      addFiles(e.target.files);
    }
  }

  // Three independent nets so the picker-open overlay can NEVER hang on cancel:
  // (1) the standard `cancel` event (modern iOS 16.4+/Chrome/Firefox), (2) focus
  // / visibility return while still armed (older devices with no `cancel`),
  // (3) a hard timeout backstop armed at open. A real selection disarms via
  // onPick first, so none fire on the happy path.
  useEffect(() => {
    if (!picking) return;
    const el = inputRef.current;
    const onCancel = () => clearPicking();
    const onReturn = () => {
      if (document.hidden) return;
      // FIX #3c — longer quiet window + require no non-empty change arrived, so a
      // still-resolving Samsung pick (empty change → populated change) isn't
      // cancelled in the gap.
      setTimeout(() => {
        if (pickerArmedRef.current && !sawNonEmptyChangeRef.current) clearPicking();
      }, 700);
    };
    el?.addEventListener('cancel', onCancel);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      el?.removeEventListener('cancel', onCancel);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [picking, clearPicking]);

  // Clear a pending safety timer on unmount.
  useEffect(() => () => { if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current); }, []);
  function onDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }
  function onDragOver(e: React.DragEvent) { e.preventDefault(); setDragOver(true); }
  function onDragLeave() { setDragOver(false); }

  // Remove a still-queued (not-yet-started) file. In the upload-as-picked model
  // there's no "staged" holding area, but a file waiting for a free pump slot
  // ('queued') can still be pulled out before it uploads.
  function removeQueued(id: string) {
    queueRef.current = queueRef.current.filter((q) => q !== id);
    setFiles((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }

  // ── Per-file memory release (THE core fix) ────────────────────────────────
  // The moment a file is fully uploaded (or fails for good), drop ALL heavy
  // references — File objects + prepared bytes + the decoded preview blob — so
  // at most ~UPLOAD_PARALLEL File objects are ever resident. Keep only a light
  // record for display: status + photo id + (cheap) server thumbnail URL.
  const releaseHeavy = useCallback((id: string, patch: Partial<FileItem>) => {
    setFiles((prev) => prev.map((f) => {
      if (f.id !== id) return f;
      if (f.previewUrl) { try { URL.revokeObjectURL(f.previewUrl); } catch { /* noop */ } }
      return {
        ...f,
        ...patch,
        file: null,
        originalFile: null,
        prepared: null,
        previewUrl: null,
        released: true,
      };
    }));
  }, []);

  // ── Upload one file (the per-file pipeline — prep + transport UNCHANGED) ──
  // Reads token/target-session/batch-id through refs so the fn identity is
  // stable for the pump. Returns nothing; updates `files` state as it goes.
  const processOne = useCallback(async (id: string) => {
    const item = filesRef.current.find((f) => f.id === id);
    if (!item) return;
    // Bytes for this file. `originalFile` is the source; on a retry after a
    // transient fail the original is still held (not released), so retry reuses
    // it. If it was released (final fail), the caller surfaces a re-pick.
    const originalFile = item.originalFile;
    if (!originalFile) {
      setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'error', error: 'Photo à re-sélectionner' } : f));
      return;
    }
    const token = tokenRef.current;
    const targetSession = selectedSessionRef.current ?? sessionsRef.current[0];
    if (!targetSession) {
      setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'error', error: 'Album introuvable' } : f));
      return;
    }
    const submissionBatchId = submissionBatchIdRef.current;

    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'uploading', progress: 1 } : f));

    // ONE upload attempt. Each potentially-hanging step is bounded so this whole
    // function always settles; a hang → reject → the retry/finally runs and the
    // pump slot frees. Throws NonRetryableUploadError on a hard 4xx (dedup/bad req).
    const runUpload = async (): Promise<void> => {
      // 1. Prep + extract metadata in parallel. (prepFile / extractAllMeta and
      //    the whole pipeline below are UNCHANGED — just called here. prepFile's
      //    img.decode() is now bounded inside heicToJpegViaCanvas.)
      const [prepared, meta] = await Promise.all([
        item.prepared ?? prepFile(originalFile),
        extractAllMeta(originalFile),
      ]);
      const contentType = resolveContentType(prepared);

      // 2. Signed URLs from the worker.
      const content_hash = await withTimeout(sha256Hex(prepared), HASH_TIMEOUT_MS, 'sha256')
        .catch(() => null);
      let signed;
      try {
        // RACE the mint — a hung fetch on flaky cellular rejects instead of
        // holding the pump slot forever.
        signed = await withTimeout(
          uploadLinkApi.photoUploadUrl(token, {
            session_id: targetSession.id,
            filename: prepared.name,
            content_type: contentType,
            submission_batch_id: submissionBatchId,
            content_hash,
            file_size_kb: Math.round(prepared.size / 1024),
          }),
          UPLOAD_URL_TIMEOUT_MS,
          'upload-url',
        );
      } catch (e) {
        // A 4xx is a hard client error — don't retry it. The thrown Error
        // message is `HTTP <status>` (no status prop), so detect 4xx from it.
        const msg = (e as Error).message ?? '';
        const status = (e as { status?: number }).status ?? Number((msg.match(/HTTP (\d{3})/) ?? [])[1]);
        if (typeof status === 'number' && status >= 400 && status < 500) {
          throw new NonRetryableUploadError(msg);
        }
        throw e;
      }

      // Silent dedup (FIX 1): worker rejects re-uploads before any row/storage.
      // Mark done + flag, and RELEASE — its bytes are no longer needed.
      if (signed.duplicate) {
        releaseHeavy(id, { status: 'done', progress: 100, duplicate: true });
        return;
      }
      if (!signed.upload_url || !signed.photo_id) {
        throw new Error('Réponse upload invalide');
      }
      const uploadUrl = signed.upload_url;
      const photoId = signed.photo_id;
      // Stamp the photo id immediately so a transport failure still leaves a
      // light record pointing at the (orphan-cleaned) row.
      setFiles((prev) => prev.map((f) => f.id === id ? { ...f, photoId, prepared } : f));

      // 3. Upload the original (the heavy 3600px file) to Storage.
      //    Prefer RESUMABLE (TUS) when the worker returned a `tus` block — a
      //    dropped connection resumes from the last offset instead of losing
      //    the whole file. On ANY TUS error, FALL BACK to the existing
      //    signed-URL PUT. Both run inside putOrCleanupOrphan so a total
      //    failure still cleans the orphan row.
      const onPutProgress = (p: number) =>
        setFiles((prev) => prev.map((f) => f.id === id ? { ...f, progress: p } : f));
      const tusBlock = signed.tus;
      await putOrCleanupOrphan(
        photoId,
        async () => {
          if (tusBlock) {
            try {
              await uploadViaTus(prepared, {
                storageHost: tusBlock.storage_host,
                anonKey: tusBlock.anon_key,
                token: tusBlock.token,
                bucket: tusBlock.bucket,
                objectPath: tusBlock.object_path,
                contentType,
                onProgress: onPutProgress,
                getFreshToken: () => uploadLinkApi.tusRefresh(token, tusBlock.object_path),
              });
              return;
            } catch {
              // Resumable upload failed — fall through to the signed-URL PUT.
              onPutProgress(0);
            }
          }
          await uploadToSignedUrl(uploadUrl, prepared, contentType, onPutProgress);
        },
        (delId) => photosApi.delete(delId),
      );

      // Fix 2 — a transport "success" (TUS onSuccess can be byteless) is not
      // proof; SERVER byte-check before this photo may count as done. Not
      // confirmed → throw so the retry loop re-runs this file (bytes are only
      // released on success).
      const landed = await confirmOriginalLanded(
        () => withTimeout(photosApi.verifyOriginal(photoId), 15_000, 'verify-original'),
      );
      if (!landed) throw new Error('Original non confirmé en storage — nouvel essai');

      // 4. HEIC preview path (best-effort, UNCHANGED).
      let previewUploaded = false;
      if (meta.previewBlob && signed.preview_upload_url) {
        try {
          const previewFile = new File([meta.previewBlob], 'preview.jpg', { type: 'image/jpeg' });
          await uploadToSignedUrl(signed.preview_upload_url, previewFile, 'image/jpeg');
          previewUploaded = true;
        } catch { /* original still safe; just no preview */ }
      }

      // 5. Thumbnail path (FIX 3, best-effort, UNCHANGED).
      let thumbnailUploaded = false;
      let tileUploaded = false;
      if (signed.thumbnail_upload_url) {
        try {
          const thumbBlob = await makeThumbnail(prepared, DISPLAY_MAX_DIM, DISPLAY_JPEG_QUALITY);
          if (thumbBlob) {
            const thumbFile = new File([thumbBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
            await uploadToSignedUrl(signed.thumbnail_upload_url, thumbFile, 'image/jpeg');
            thumbnailUploaded = true;

            // MIGRATION 128 — the ~320px tray tile, downscaled from the blob we
            // just built rather than from `prepared`: already decoded, already
            // the right aspect, and no second full-size decode on a phone.
            // Replaces a BILLED image-transform (worker lib/photoTiers.ts).
            if (signed.tile_upload_url) {
              const tileBlob = await makeThumbnail(thumbFile, TILE_MAX_DIM, TILE_JPEG_QUALITY);
              if (tileBlob) {
                await uploadToSignedUrl(
                  signed.tile_upload_url,
                  new File([tileBlob], 'tile.jpg', { type: 'image/jpeg' }),
                  'image/jpeg',
                );
                tileUploaded = true;
              }
            }
          }
        } catch { /* editor falls back to preview/original */ }
      }

      // 6. Save metadata (best-effort, UNCHANGED).
      try {
        await uploadLinkApi.photoMetadata(token, photoId, {
          exif_date: meta.exifDate,
          exif_gps_lat: meta.gpsLat,
          exif_gps_lng: meta.gpsLng,
          width: meta.width,
          height: meta.height,
          file_size_kb: Math.round(prepared.size / 1024),
          preview_uploaded: previewUploaded,
          preview_storage_path: previewUploaded ? signed.preview_storage_path : null,
          thumbnail_storage_path: thumbnailUploaded ? signed.thumbnail_storage_path : null,
          tile_storage_path: tileUploaded ? signed.tile_storage_path : null,
        });
      } catch { /* swallow — photo still uploaded successfully */ }

      // DONE → release every heavy byte. The light record (status/photoId)
      // carries the ✓ tile; the UploadedGallery below shows the real server
      // thumbnail on its own refetch.
      releaseHeavy(id, { status: 'done', progress: 100, photoId });
    };

    // HARD per-file backstop + retry. The backstop (PER_FILE_TIMEOUT_MS)
    // GUARANTEES runUpload settles so the pump's `.finally` ALWAYS runs and the
    // slot frees. A hung/5xx/network attempt retries up to UPLOAD_MAX_RETRIES
    // with backoff; a hard 4xx (NonRetryableUploadError) fails immediately. On a
    // final failure the bytes are KEPT (not released) so a one-tap Réessayer can
    // re-run THIS file. NEVER throws — the slot can't be lost on any path.
    for (let attempt = 0; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        await withTimeout(runUpload(), PER_FILE_TIMEOUT_MS, `processOne ${originalFile.name}`);
        return; // success/dedup — slot frees via the pump's .finally
      } catch (err) {
        const nonRetryable = err instanceof NonRetryableUploadError;
        const isLast = attempt >= UPLOAD_MAX_RETRIES || nonRetryable;
        if (isLast) {
          // Transient/hard failure: KEEP the bytes so a one-tap retry can re-run
          // THIS file without re-picking. (Released only on success / dedup.)
          setFiles((prev) => prev.map((f) => f.id === id
            ? { ...f, status: 'error', error: (err as Error).message }
            : f));
          return;
        }
        const backoff = UPLOAD_RETRY_BACKOFF_MS[attempt] ?? 3_000;
        setFiles((prev) => prev.map((f) => f.id === id
          ? { ...f, status: 'uploading', progress: 1, error: undefined } : f));
        await new Promise((r) => setTimeout(r, backoff));
      }
    }
  }, [releaseHeavy]);

  // ── The pump — bounded-concurrency, re-entrant, idempotent ────────────────
  // Pulls ids off queueRef and runs up to UPLOAD_PARALLEL processOne() at once.
  // runningRef guarantees only ONE pump loop is scheduling at a time: a second
  // call while it's running is a no-op (the loop already tops up free slots as
  // each upload settles). So adding photos mid-upload can't double-pump or drop
  // items. activeRef is the source of truth for "everything finished".
  const pump = useCallback(() => {
    if (runningRef.current) return;       // re-entrancy guard
    runningRef.current = true;
    const launch = () => {
      while (activeRef.current < UPLOAD_PARALLEL_EFFECTIVE && queueRef.current.length > 0) {
        const id = queueRef.current.shift()!;
        // Skip ids that were removed/retried away while queued.
        const live = filesRef.current.find((f) => f.id === id);
        if (!live || live.status !== 'queued') continue;
        activeRef.current++;
        void processOne(id).finally(() => {
          activeRef.current--;
          launch();                        // free slot → pull the next id
        });
      }
    };
    launch();
    runningRef.current = false;
  }, [processOne]);
  useEffect(() => { pumpRef.current = pump; }, [pump]);

  // Retry a single failed file. If its bytes are still held (transient fail),
  // re-queue it and re-pump — no re-pick needed. If they were released (which
  // only happens on success/dedup, never on a fail), surface a re-pick.
  const retryOne = useCallback((id: string) => {
    const item = filesRef.current.find((f) => f.id === id);
    if (!item || item.status !== 'error') return;
    if (item.released || !item.originalFile) {
      flashAddNotice('Cette photo doit être re-sélectionnée.');
      return;
    }
    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: 'queued', progress: 0, error: undefined } : f));
    // Sync mirror for the same reason as addFiles — the pump reads filesRef NOW.
    filesRef.current = filesRef.current.map((f) => f.id === id ? { ...f, status: 'queued' as const, progress: 0, error: undefined } : f);
    queueRef.current.push(id);
    pumpRef.current?.();
  }, [flashAddNotice]);

  // "Continuer" — a PROCEED action, NOT an upload trigger (uploads already ran
  // on pick). Only enabled once ≥ MIN_PHOTOS uploaded and nothing is in flight.
  const handleContinue = useCallback(() => {
    if (submitStateRef.current === 'done') return;
    setSubmitState('done');
  }, []);

  // ── Cleanup blob URLs on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); if (f.thumbUrl) URL.revokeObjectURL(f.thumbUrl); });
    };
  }, []);

  // ── Aggregates ───────────────────────────────────────────────────────────
  const total = files.length;
  const done = files.filter((f) => f.status === 'done').length;
  const errored = files.filter((f) => f.status === 'error').length;
  const inFlight = files.filter((f) => f.status === 'uploading').length;
  const queued = files.filter((f) => f.status === 'queued').length;
  // Still working = anything not yet settled (queued or in flight).
  const working = queued + inFlight;
  // Uploaded successfully (incl. silently-deduped) — the count that gates
  // "Continuer" and drives the live "X / Y importées" line.
  const uploaded = done;
  const overallProgress = total === 0 ? 0 :
    Math.round(files.reduce((sum, f) => {
      if (f.status === 'done') return sum + 100;
      if (f.status === 'uploading') return sum + Math.max(1, f.progress);
      return sum;
    }, 0) / total);

  // The MIN_PHOTOS gate is about the album's TOTAL — a customer who already
  // sent photos in an earlier batch (or on an earlier visit) shouldn't be
  // forced to add 20 MORE. `photo_count` is the server-side count from resolve
  // (photos uploaded BEFORE this batch); it's stable during the batch
  // (refreshSessions only runs after "Continuer"), so adding this batch's
  // `uploaded` gives the live album total with no double-count.
  const albumAlreadyUploaded = selectedSession?.photo_count ?? 0;
  const albumTotalUploaded = albumAlreadyUploaded + uploaded;

  // "Continuer" is enabled once the album's TOTAL reaches the minimum AND
  // nothing in this batch is still working. Errored files don't block — the
  // customer can retry or proceed without them.
  const canProceed = albumTotalUploaded >= MIN_PHOTOS && working === 0;

  // Confetti + post-success refreshes fire when the customer taps "Continuer"
  // (submitState → 'done'). Uploads have already completed by then.
  const confettiFiredRef = useRef(false);
  useEffect(() => {
    if (submitState === 'done' && !confettiFiredRef.current && total > 0) {
      confettiFiredRef.current = true;
      fireConfetti();
      // Refresh per-album counts so when the client returns to the selection
      // screen the just-uploaded album shows its new total.
      if (!isSingleAlbum) void refreshSessions();
      // Refetch the review gallery so the just-uploaded photos appear in it.
      setGalleryRefreshKey((k) => k + 1);
    }
  }, [submitState, total, isSingleAlbum, refreshSessions]);

  // Return to the album-selection screen (multi-album) after a successful
  // batch, or to reselect an album. Clears the staged/done batch + resets the
  // submit lifecycle so a fresh selection can begin for the next album.
  const backToAlbumSelection = useCallback(() => {
    filesRef.current.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); if (f.thumbUrl) URL.revokeObjectURL(f.thumbUrl); });
    queueRef.current = [];
    submissionBatchIdRef.current = crypto.randomUUID(); // fresh batch for the next album
    setFiles([]);
    confettiFiredRef.current = false;
    setSubmitState('idle');
    setSelectedAlbumIndex(null);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return <ShellCenter><Spinner /></ShellCenter>;

  if (linkError) {
    return (
      <ShellCenter>
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md text-center" style={{ borderColor: BRAND.border }}>
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-xl font-semibold mb-2" style={{ color: BRAND.ink }}>Lien indisponible</h1>
          <p className="text-sm" style={{ color: BRAND.muted }}>{linkError}</p>
          <p className="text-xs mt-4" style={{ color: BRAND.muted }}>
            Demandez un nouveau lien à Storiz sur WhatsApp.
          </p>
        </div>
      </ShellCenter>
    );
  }

  // After successful submit: full-page success state.
  // Customer can add MORE photos from the same link (separate submission batch
  // in admin) by clicking "Envoyer plus de photos" — that wipes the current
  // batch from the UI and resets to idle so a fresh selection can begin.
  if (submitState === 'done') {
    const duplicateCount = files.filter((f) => f.duplicate).length;
    const submittedCount = total - duplicateCount;
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: `linear-gradient(180deg, ${BRAND.cream} 0%, ${BRAND.creamSoft} 100%)`,
      }}>
        <div className="max-w-md w-full">
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center" style={{ border: `1px solid ${BRAND.border}` }}>
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5 shadow-lg" style={{
              background: `linear-gradient(135deg, ${BRAND.success} 0%, #1F6B40 100%)`,
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: BRAND.ink, fontFamily: 'Georgia, serif' }}>
              {submittedCount} photo{submittedCount > 1 ? 's' : ''} envoyée{submittedCount > 1 ? 's' : ''} ✨
            </h1>
            <p className="text-sm mb-1" style={{ color: BRAND.muted }}>
              Reçues par l'équipe Storiz.
            </p>
            <p className="text-sm" style={{ color: BRAND.muted }}>
              Nous vous contactons dès que votre album est prêt 📦
            </p>
            {duplicateCount > 0 && (
              <p className="text-xs mt-2" style={{ color: BRAND.muted }}>
                {duplicateCount} photo{duplicateCount > 1 ? 's' : ''} en double ignorée{duplicateCount > 1 ? 's' : ''}
              </p>
            )}

            {/* PRIMARY next step — into the STEPS FLOW (cover → Magie AI →
                aperçu). The flow's resume logic lands on the right step for
                the uploaded photos; the AI-Magic step then builds the album
                (model templates ON by default). Without this the upload page
                was a dead end ("we'll contact you"). */}
            {order?.shopify_order_id && (
              <a
                href={`/flow/${order.shopify_order_id}?step=mode`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-base font-bold text-white transition active:scale-[0.99] shadow-lg"
                style={{ background: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldDeep} 100%)` }}
              >
                ✨ Créer mon album maintenant →
              </a>
            )}

            <button
              type="button"
              onClick={() => {
                // Reset to idle so the customer can stage a new batch. The
                // already-uploaded files stay on the server (admin sees them
                // as a separate submission batch). Multi-album returns to the
                // album-selection screen (with refreshed counts); single-album
                // returns straight to the upload UI.
                if (!isSingleAlbum) {
                  backToAlbumSelection();
                  return;
                }
                files.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); if (f.thumbUrl) URL.revokeObjectURL(f.thumbUrl); });
                queueRef.current = [];
                submissionBatchIdRef.current = crypto.randomUUID(); // fresh batch
                setFiles([]);
                confettiFiredRef.current = false;
                setSubmitState('idle');
              }}
              className="mt-3 w-full px-4 py-3 rounded-xl text-sm font-semibold border-2 transition active:scale-[0.99]"
              style={{ borderColor: BRAND.gold, color: BRAND.goldDeep, background: '#fff' }}
            >
              {isSingleAlbum ? '+ Envoyer plus de photos' : '← Choisir un autre album'}
            </button>

            <div className="mt-6 pt-6 border-t flex items-center justify-center gap-2 text-xs" style={{ borderColor: BRAND.border, color: BRAND.muted }}>
              <span>Suivi de votre commande sur</span>
              <a href="https://wa.me/212620066907" target="_blank" rel="noopener noreferrer" className="font-semibold underline" style={{ color: BRAND.goldDeep }}>
                WhatsApp
              </a>
            </div>
          </div>

          <p className="text-center text-xs mt-4" style={{ color: BRAND.muted }}>
            Vous pouvez fermer cette page en toute sécurité.
          </p>
        </div>
      </div>
    );
  }

  // ── Album selection (Duo/Trio only, before an album is chosen) ───────────
  // Solo skips this entirely: `isSingleAlbum` auto-selects sessions[0] and we
  // fall straight through to the upload UI below.
  if (!isSingleAlbum && !selectedSession) {
    return (
      <AlbumSelectionScreen
        order={order}
        sessions={sessions}
        onSelect={(albumIndex) => setSelectedAlbumIndex(albumIndex)}
        editingSessionId={editingSessionId}
        titleError={titleError}
        onStartEdit={startTitleEdit}
        onCancelEdit={cancelTitleEdit}
        onSaveTitle={saveAlbumTitle}
      />
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{
      background: `linear-gradient(180deg, ${BRAND.cream} 0%, ${BRAND.creamSoft} 35%, #fff 100%)`,
    }}>
      {/* iOS picker-open overlay — visible from the moment the customer taps
          the dropzone until `change`/`cancel`/timeout, so the page never looks
          frozen while iOS downloads their iCloud originals. */}
      {picking && (
        <div
          aria-live="polite"
          className="fixed inset-0 z-[100] flex items-center justify-center px-8 text-center"
          style={{ background: 'rgba(31,37,41,0.55)', backdropFilter: 'blur(6px)' }}
        >
          <div className="rounded-3xl px-8 py-9 max-w-xs w-full shadow-2xl bg-white">
            <div
              className="w-12 h-12 mx-auto mb-5 rounded-full"
              style={{
                border: `3px solid ${BRAND.cream}`,
                borderTopColor: BRAND.gold,
                animation: 'storizSpin 0.8s linear infinite',
              }}
            />
            <div className="text-base font-semibold" style={{ color: BRAND.ink }}>
              Chargement de vos photos…
            </div>
            <div className="text-sm mt-2" style={{ color: BRAND.muted }}>
              ça peut prendre quelques secondes 📸
            </div>
          </div>
        </div>
      )}

      {/* Brand header */}
      <header className="px-4 py-4 sm:px-6 sm:py-5 flex items-center justify-between border-b" style={{
        borderColor: BRAND.border,
        background: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(8px)',
      }}>
        <div className="flex items-center gap-2">
          <img src="/storiz-logo.svg" alt="Storiz" className="h-7 sm:h-8" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span className="font-bold tracking-wide text-lg" style={{ color: BRAND.ink, fontFamily: 'Georgia, serif' }}>STORIZ</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: BRAND.gold }}>
          Album Premium
        </span>
      </header>

      {/* Breadcrumb — shows which album the client is adding photos to + an
          inline title rename (pencil). Multi-album also gets a Back button to
          reselect (disabled during an in-flight upload so the client can't
          switch albums mid-batch). Rendered for Solo too so the title stays
          editable there. */}
      {selectedSession && (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-4">
          <div className="flex items-center gap-2 text-sm min-w-0">
            {!isSingleAlbum && (
              <button
                type="button"
                onClick={backToAlbumSelection}
                disabled={working > 0}
                aria-label="Retour à la sélection des albums"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl font-semibold border transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                style={{ borderColor: BRAND.border, color: BRAND.goldDeep, background: '#fff', minHeight: 44 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Retour
              </button>
            )}
            {editingSessionId === selectedSession.id ? (
              <AlbumTitleInput
                initial={selectedSession.destination ?? ''}
                onCommit={(value) => saveAlbumTitle(selectedSession.id, value)}
                onCancel={cancelTitleEdit}
              />
            ) : (
              <span className="flex items-center gap-0.5 min-w-0" style={{ color: BRAND.muted }}>
                <span className="font-semibold truncate" style={{ color: BRAND.ink }}>
                  Album {selectedSession.album_index}
                  {selectedSession.destination ? ` — ${selectedSession.destination}` : ''}
                  {selectedSession.year ? ` ${selectedSession.year}` : ''}
                </span>
                <PencilButton onClick={() => startTitleEdit(selectedSession.id)} />
                <span className="shrink-0" style={{ color: BRAND.muted }}>› Ajouter des photos</span>
              </span>
            )}
          </div>
          {titleError?.sessionId === selectedSession.id && (
            <div className="text-xs mt-1" style={{ color: '#DC2626' }}>{titleError.message}</div>
          )}
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        {/* Greeting */}
        <div className="text-center mb-7">
          <div className="inline-block px-3 py-1 mb-3 rounded-full text-[10px] uppercase tracking-widest font-semibold" style={{ background: BRAND.gold, color: '#fff' }}>
            Vos photos · pas de compte requis
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ color: BRAND.ink, fontFamily: 'Georgia, serif' }}>
            {order?.customer_name
              ? <>Bienvenue <span style={{ color: BRAND.goldDeep }}>{order.customer_name.split(' ')[0]}</span> 👋</>
              : <>Bienvenue 👋</>}
          </h1>
          <p className="text-sm mt-2" style={{ color: BRAND.muted }}>
            Sélectionnez vos photos puis envoyez-les en un clic.
          </p>
        </div>

        {/* Order context */}
        {order && (
          <div className="bg-white rounded-2xl border p-4 sm:p-5 mb-5 shadow-sm" style={{ borderColor: BRAND.border }}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
              {order.bundle && <Stat label="Formule" value={`${order.bundle} · ${order.page_count} papier`} />}
              {order.destinations.map((d, i) => (
                <Stat key={i} label={`Destination ${i + 1}`} value={`${d.name}${d.year ? ` · ${d.year}` : ''}`} />
              ))}
            </div>
            {expiresAt && (
              <div className="text-xs mt-3 pt-3 border-t" style={{ color: BRAND.muted, borderColor: BRAND.border }}>
                Lien valide jusqu'au {new Date(expiresAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
          </div>
        )}

        {/* Drop zone — always open for adding more (uploads run as picked; new
            files just join the in-flight queue). */}
        <div
          onClick={() => {
            // Arm the iOS overlay (bridges the picker-open → change gap while
            // iCloud originals download) + the cancel fallbacks + the hard
            // timeout, THEN open. The timeout/cancel/focus nets guarantee it
            // can't hang if the picker is dismissed with no selection.
            // FIX #3a — reset value on OPEN (not in onPick) so a same-file re-pick
            // still fires `change`, and a Samsung empty-first-`change` can't wipe a
            // pick that's about to be delivered by the next, populated `change`.
            pickerArmedRef.current = true;
            sawNonEmptyChangeRef.current = false;
            if (inputRef.current) inputRef.current.value = '';
            if (isIosDevice()) setPicking(true);
            if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = setTimeout(clearPicking, PICKER_SAFETY_MS);
            inputRef.current?.click();
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className="relative rounded-3xl border-2 border-dashed transition-all p-8 sm:p-10 text-center bg-white overflow-hidden"
          style={{
            borderColor: dragOver ? BRAND.gold : BRAND.border,
            background: dragOver ? '#fffaf0' : '#fff',
            cursor: 'pointer',
            opacity: 1,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={galleryAccept()}
            onChange={onPick}
            className="hidden"
          />
          <div className="relative">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full flex items-center justify-center mb-4 shadow-lg" style={{
              background: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldDeep} 100%)`,
              animation: 'storizGlow 2.4s ease-in-out infinite',
            }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div className="text-base font-semibold" style={{ color: BRAND.ink }}>
              Cliquez ou glissez vos photos ici
            </div>
            <div className="text-sm font-medium mt-1 underline" style={{ color: BRAND.goldDeep }}>
              Ou choisir des fichiers
            </div>
            <div className="text-xs mt-3" style={{ color: BRAND.muted }}>
              JPG, PNG, HEIC (iPhone) — toutes tailles acceptées
            </div>
          </div>
        </div>

        {/* iOS-only iCloud import reassurance — sits just under the dropzone,
            deepens while uploads are in flight. Self-hides off iOS. */}
        <IosUploadNote active={working > 0} style={{ marginTop: 8 }} />

        {/* Inline add-files notice — explains why a selection produced nothing
            (empty FileList, still on iCloud, non-image) instead of the silent
            no-op the original handler did. Auto-dismisses after 8 s. */}
        {addNotice && (
          <div
            className="mt-3 rounded-2xl px-4 py-3 text-sm leading-snug"
            style={{ background: `${BRAND.gold}12`, color: BRAND.ink, border: `1px solid ${BRAND.gold}30` }}
          >
            {addNotice}
          </div>
        )}

        {/* Review + delete gallery — the photos already uploaded to THIS album.
            Keyed to selectedSession.album_index so a Trio link shows (and
            deletes from) only the chosen album. */}
        {selectedSession && (
          <UploadedGallery
            key={selectedSession.album_index}
            token={token}
            albumIndex={selectedSession.album_index}
            refreshKey={galleryRefreshKey}
          />
        )}

        {/* Live import progress (upload-as-picked). Shows as soon as any photo
            is in this batch and stays until the customer proceeds. */}
        {total > 0 && (
          <div className="mt-5 bg-white rounded-2xl border p-4 sm:p-5 shadow-sm" style={{ borderColor: BRAND.border }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {working > 0 && (
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inset-0 rounded-full opacity-75 animate-ping" style={{ background: BRAND.gold }} />
                    <span className="relative w-2 h-2 rounded-full" style={{ background: BRAND.gold }} />
                  </span>
                )}
                <span className="text-sm font-semibold" style={{ color: BRAND.ink }}>
                  {uploaded} / {total} importées
                </span>
              </div>
              <span className="text-sm font-bold" style={{ color: BRAND.goldDeep }}>{overallProgress}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: BRAND.creamSoft }}>
              <div
                className="h-full rounded-full transition-all duration-300 relative overflow-hidden"
                style={{
                  width: `${overallProgress}%`,
                  background: `linear-gradient(90deg, ${BRAND.gold} 0%, ${BRAND.goldDeep} 100%)`,
                }}
              >
                {working > 0 && (
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)',
                    animation: 'storizShimmer 1.4s linear infinite',
                  }} />
                )}
              </div>
            </div>
            <div className="text-xs mt-2 flex gap-3 flex-wrap" style={{ color: BRAND.muted }}>
              {inFlight > 0 && <span>↑ {inFlight} en cours</span>}
              {queued > 0 && <span>· {queued} en attente</span>}
              {errored > 0 && <span style={{ color: '#B91C1C' }}>· {errored} échec{errored > 1 ? 's' : ''}</span>}
              {working === 0 && errored === 0 && uploaded > 0 && (
                <span style={{ color: BRAND.success }}>✓ tout est importé</span>
              )}
            </div>
            {albumTotalUploaded < MIN_PHOTOS && (
              <div className="text-xs mt-2" style={{ color: BRAND.muted }}>
                Minimum {MIN_PHOTOS} photos — encore {Math.max(0, MIN_PHOTOS - albumTotalUploaded)} à ajouter.
              </div>
            )}
          </div>
        )}

        {/* File grid — VIRTUALIZED (react-window). Only on-screen tiles mount,
            so the DOM stays tiny no matter how many photos were picked. A
            finished tile shows just a ✓ status (its heavy preview blob has been
            released); the UploadedGallery above carries the real thumbnails. */}
        {files.length > 0 && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.muted }}>
                {total} photo{total > 1 ? 's' : ''} · {uploaded} importée{uploaded > 1 ? 's' : ''}
              </span>
            </div>
            <WindowedTileGrid
              files={files}
              onRemove={removeQueued}
              onRetry={retryOne}
            />
          </div>
        )}

        {/* Empty hint */}
        {files.length === 0 && (
          <div className="text-center mt-5 text-xs" style={{ color: BRAND.muted }}>
            Vos photos apparaissent ici dès que vous les sélectionnez.
          </div>
        )}
      </div>

      {/* ─── Sticky proceed bar ─────────────────────────────────────────────
          "Continuer" is a PROCEED action — uploads already ran on pick. It's
          enabled once the minimum is uploaded and nothing is still working.
          Shown as soon as there's anything in this batch. */}
      {total > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-50 px-4 pb-4 pt-3" style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.95) 35%, #fff 100%)',
          backdropFilter: 'blur(6px)',
        }}>
          <div className="max-w-2xl mx-auto">
            <button
              type="button"
              onClick={handleContinue}
              disabled={!canProceed}
              className="w-full rounded-2xl px-5 py-4 font-bold text-white text-base shadow-xl transition-all active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background: !canProceed
                  ? `linear-gradient(135deg, ${BRAND.muted} 0%, #5e5544 100%)`
                  : `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldDeep} 100%)`,
              }}
            >
              {working > 0 ? (
                <>
                  <Spinner small />
                  Import en cours · {uploaded}/{total}
                </>
              ) : albumTotalUploaded < MIN_PHOTOS ? (
                <>
                  Ajoutez au moins {MIN_PHOTOS} photos ({albumTotalUploaded}/{MIN_PHOTOS})
                </>
              ) : (
                <>
                  Continuer →
                </>
              )}
            </button>
            <p className="text-center text-[11px] mt-2" style={{ color: BRAND.muted }}>
              Vos photos sont importées au fur et à mesure · rien à attendre
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes storizGlow {
          0%, 100% { box-shadow: 0 8px 24px rgba(201,162,74,0.30), 0 0 0 0 rgba(201,162,74,0.55); }
          50%      { box-shadow: 0 8px 24px rgba(201,162,74,0.45), 0 0 0 14px rgba(201,162,74,0); }
        }
        @keyframes storizShimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes storizSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Unified support widget. Lifts above the sticky "Continuer" bar when
          it's showing (bar ≈ 88px) so it never sits on the CTA. */}
      <HelpFAB bottomOffset={total > 0 ? 100 : 16} />
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

// ─── Windowed tile grid ──────────────────────────────────────────────────────
// react-window v2 windowed square grid for the in-progress upload tiles. Only
// on-screen rows mount, so the DOM (and its memory) stays flat no matter how
// many photos were picked — the other half of the iOS RAM fix. Sits in a fixed
// max-height scroll viewport (react-window needs a numeric pixel height) and
// derives column count from the live container width, mirroring the editor's
// VirtualPhotoGrid pattern.
interface TileRowProps {
  columns: number;
  count: number;
  gap: number;
  files: FileItem[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

function TileRow({
  index: rowIndex,
  style,
  columns,
  count,
  gap,
  files,
  onRemove,
  onRetry,
}: RowComponentProps<TileRowProps>) {
  const start = rowIndex * columns;
  const tiles: ReactNode[] = [];
  for (let col = 0; col < columns; col++) {
    const i = start + col;
    if (i >= count) break;
    const f = files[i];
    tiles.push(
      <div key={f.id} style={{ flex: `0 0 calc((100% - ${(columns - 1) * gap}px) / ${columns})`, minWidth: 0 }}>
        <FileTile item={f} onRemove={() => onRemove(f.id)} onRetry={() => onRetry(f.id)} />
      </div>,
    );
  }
  return (
    <div style={{ ...style, display: 'flex', gap, paddingBottom: gap, alignItems: 'flex-start' }}>
      {tiles}
    </div>
  );
}

function WindowedTileGrid({ files, onRemove, onRetry }: {
  files: FileItem[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const gap = 8;
  const minTile = 96; // ≈ the old 6rem minmax floor
  const columns = Math.max(1, Math.floor((width + gap) / (minTile + gap)));
  const count = files.length;
  const rowCount = Math.ceil(count / columns);
  const tileW = columns > 0 ? (width - (columns - 1) * gap) / columns : minTile;
  const rowHeight = Math.max(1, Math.round(tileW + gap));

  // Cap the scroll viewport so very large batches stay in a fixed window
  // (mirrors the UploadedGallery's max-h). Small batches shrink to fit.
  const maxViewport = Math.round(window.innerHeight * 0.6);
  const fullHeight = rowCount * rowHeight;
  const viewport = Math.min(maxViewport, Math.max(rowHeight, fullHeight));

  const rowProps = useMemo<TileRowProps>(
    () => ({ columns, count, gap, files, onRemove, onRetry }),
    [columns, count, gap, files, onRemove, onRetry],
  );

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      {width > 0 && count > 0 && (
        <VirtualList
          rowComponent={TileRow}
          rowCount={rowCount}
          rowHeight={rowHeight}
          rowProps={rowProps}
          overscanCount={2}
          style={{ height: viewport, width: '100%' }}
        />
      )}
    </div>
  );
}

function FileTile({ item, onRemove, onRetry }: {
  item: FileItem;
  onRemove: () => void;
  onRetry: () => void;
}) {
  const isDone    = item.status === 'done';
  const isErr     = item.status === 'error';
  const isUp      = item.status === 'uploading';
  const isQueued  = item.status === 'queued';

  // Full-res blob preview (instant, freed on finish) → falls back to the small
  // PERSISTENT thumbnail that survives the upload's RAM release. So the
  // customer keeps seeing their photo after it's done, with only a corner ✓.
  const displaySrc = item.previewUrl ?? item.thumbUrl;

  return (
    <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 shadow-sm group">
      {displaySrc ? (
        <img src={displaySrc} alt="" className="w-full h-full object-cover" loading="lazy" />
      ) : isDone ? (
        // No thumbnail could be generated (rare decode failure) — clean ✓ tile.
        <div className="w-full h-full flex items-center justify-center" style={{ background: `${BRAND.success}14` }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center shadow" style={{ background: BRAND.success, color: '#fff' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center px-2" style={{ background: BRAND.creamSoft }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND.gold} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <div className="text-[10px] font-semibold mt-1" style={{ color: BRAND.muted }}>Photo iPhone</div>
        </div>
      )}

      {/* Queued dim — soft tint so user knows these are waiting for a slot */}
      {isQueued && (
        <div className="absolute inset-0 bg-white/15" />
      )}

      {/* Uploading overlay */}
      {isUp && (
        <div className="absolute inset-0 bg-black/35 flex flex-col items-center justify-center text-white">
          <div className="w-10 h-10 rounded-full border-2 border-white border-t-transparent animate-spin" />
          <span className="text-xs font-bold mt-1">{item.progress}%</span>
        </div>
      )}

      {/* Error overlay — tap to retry (bytes still held) or re-pick (released) */}
      {isErr && (
        <button
          type="button"
          aria-label="Réessayer cette photo"
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white active:scale-[0.97] transition"
        >
          <div className="text-xl">⚠️</div>
          <span className="text-[10px] mt-0.5 underline">{item.released ? 're-sélectionner' : 'réessayer'}</span>
        </button>
      )}

      {/* Done check — small corner badge over the (still-visible) photo. */}
      {isDone && displaySrc && (
        <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center shadow" style={{ background: BRAND.success, color: '#fff' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      )}

      {/* Remove — only while still queued (not yet uploading). Mobile users
          can't hover, so we don't gate on group-hover. */}
      {isQueued && (
        <button
          type="button"
          aria-label="Retirer cette photo"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full text-white flex items-center justify-center text-base font-bold shadow-md active:scale-95 transition"
          style={{ background: 'rgba(0,0,0,0.7)' }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// ─── Review + delete gallery ─────────────────────────────────────────────────
// iPhone-Photos-style grid of the photos already uploaded to ONE album. Lets
// the customer review and delete before / between batches. Scoped to a single
// album_index — both the worker (session scope check) and the album-keyed
// `key` on this component guarantee deleting in album 1 can't touch albums 2/3.
type GalleryPhoto = {
  id: string;
  signed_url: string | null;
  orientation: number | null;
  width: number | null;
  height: number | null;
};

function UploadedGallery({ token, albumIndex, refreshKey }: {
  token: string;
  albumIndex: number;
  refreshKey: number;
}) {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch (and refetch on album change or after an upload batch).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const data = await uploadLinkApi.listPhotos(token, albumIndex);
        if (cancelled) return;
        setPhotos(data.photos);
        // Drop any selection that no longer exists.
        setSelected((prev) => {
          const ids = new Set(data.photos.map((p) => p.id));
          const next = new Set<string>();
          prev.forEach((id) => { if (ids.has(id)) next.add(id); });
          return next;
        });
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, albumIndex, refreshKey]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const allSelected = photos.length > 0 && selected.size === photos.length;
  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === photos.length ? new Set() : new Set(photos.map((p) => p.id)),
    );
  }, [photos]);

  const runDelete = useCallback(async (ids: string[]) => {
    if (ids.length === 0 || deleting) return;
    const ok = window.confirm(
      `Supprimer ${ids.length} photo${ids.length > 1 ? 's' : ''} ? Cette action est définitive.`,
    );
    if (!ok) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await uploadLinkApi.deletePhotos(token, albumIndex, ids);
      const removed = new Set(ids);
      setPhotos((prev) => prev.filter((p) => !removed.has(p.id)));
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    } catch (err) {
      setDeleteError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  }, [token, albumIndex, deleting]);

  // Hide the whole block until we know there's something to show — keeps the
  // upload screen clean for a brand-new (empty) album.
  if (loading) {
    return (
      <div className="mt-5 flex items-center justify-center py-6">
        <div
          className="w-6 h-6 rounded-full"
          style={{ border: `3px solid ${BRAND.cream}`, borderTopColor: BRAND.gold, animation: 'storizSpin 0.8s linear infinite' }}
        />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="mt-5 rounded-2xl px-4 py-3 text-sm" style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}>
        Impossible de charger vos photos déjà envoyées.
      </div>
    );
  }
  if (photos.length === 0) return null;

  return (
    <div className="mt-5 bg-white rounded-2xl border p-4 sm:p-5 shadow-sm" style={{ borderColor: BRAND.border }}>
      {/* Header: count + select-all toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold" style={{ color: BRAND.ink }}>
          {photos.length} photo{photos.length > 1 ? 's' : ''} déjà envoyée{photos.length > 1 ? 's' : ''}
        </div>
        <button
          type="button"
          onClick={toggleSelectAll}
          className="text-xs font-semibold underline"
          style={{ color: BRAND.goldDeep }}
        >
          {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
      </div>

      <p className="text-xs mb-3" style={{ color: BRAND.muted }}>
        Touchez une photo pour la sélectionner, puis supprimez celles que vous ne voulez pas garder.
      </p>

      {/* iPhone-Photos-style square grid, scrollable — shared, virtualized.
          Always in select mode here (tap toggles selection); the × chip also
          single-deletes. Behaviour is identical to the previous inline grid. */}
      <UploadedTileGrid
        photos={photos.map((p) => ({ id: p.id, thumbUrl: p.signed_url, selected: selected.has(p.id) }))}
        selectMode
        onToggleSelect={toggle}
        onDelete={deleting ? undefined : (id) => { void runDelete([id]); }}
      />

      {deleteError && (
        <div className="mt-3 text-xs" style={{ color: '#B91C1C' }}>
          Échec de la suppression : {deleteError}
        </div>
      )}

      {/* Delete-selection action bar */}
      {selected.size > 0 && (
        <button
          type="button"
          disabled={deleting}
          onClick={() => void runDelete([...selected])}
          className="mt-4 w-full px-4 py-3 rounded-xl text-sm font-semibold text-white transition active:scale-[0.99] disabled:opacity-60"
          style={{ background: '#DC2626', minHeight: 44 }}
        >
          {deleting
            ? 'Suppression…'
            : `Supprimer la sélection (${selected.size})`}
        </button>
      )}
    </div>
  );
}

// ─── Album selection (Duo/Trio) ──────────────────────────────────────────────
// Premium, mobile-first card grid. Whole card is one large tappable button.
// Reuses the MagicUploadPage BRAND palette + typography so the surface stays
// visually consistent with the upload UI it leads into.
function AlbumSelectionScreen({ order, sessions, onSelect, editingSessionId, titleError, onStartEdit, onCancelEdit, onSaveTitle }: {
  order: UploadLinkOrder | null;
  sessions: UploadLinkSession[];
  onSelect: (albumIndex: number) => void;
  /** Shared inline title-edit state — lives in MagicUploadPage so the upload
   *  header and these cards use ONE editor + one optimistic save path. */
  editingSessionId: string | null;
  titleError: { sessionId: string; message: string } | null;
  onStartEdit: (sessionId: string) => void;
  onCancelEdit: () => void;
  onSaveTitle: (sessionId: string, value: string) => void;
}) {
  return (
    <div className="min-h-screen pb-16" style={{
      background: `linear-gradient(180deg, ${BRAND.cream} 0%, ${BRAND.creamSoft} 35%, #fff 100%)`,
    }}>
      {/* Brand header — mirrors the upload screen header */}
      <header className="px-4 py-4 sm:px-6 sm:py-5 flex items-center justify-between border-b" style={{
        borderColor: BRAND.border,
        background: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(8px)',
      }}>
        <div className="flex items-center gap-2">
          <img src="/storiz-logo.svg" alt="Storiz" className="h-7 sm:h-8" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span className="font-bold tracking-wide text-lg" style={{ color: BRAND.ink, fontFamily: 'Georgia, serif' }}>STORIZ</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: BRAND.gold }}>
          Album Premium
        </span>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        <div className="text-center mb-7">
          <div className="inline-block px-3 py-1 mb-3 rounded-full text-[10px] uppercase tracking-widest font-semibold" style={{ background: BRAND.gold, color: '#fff' }}>
            {order?.bundle ?? 'Vos albums'} · pas de compte requis
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ color: BRAND.ink, fontFamily: 'Georgia, serif' }}>
            Touchez un album pour y ajouter vos photos 📸
          </h1>
          <p className="text-sm mt-2" style={{ color: BRAND.muted }}>
            Vous avez {sessions.length} albums. Choisissez celui à compléter.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sessions.map((s) => {
            const count = s.photo_count ?? 0;
            const title = s.destination
              ? `${s.destination}${s.year ? ` ${s.year}` : ''}`
              : `Album ${s.album_index}`;
            const countLabel = count > 0
              ? `${count} photo${count > 1 ? 's' : ''}`
              : 'En attente de photos';
            return (
              /* div role="button", not <button>: the title row nests an
                 interactive pencil button (and an <input> while editing) —
                 invalid inside a native <button>. Same classes → same look. */
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                onClick={() => { if (editingSessionId !== s.id) onSelect(s.album_index); }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                    e.preventDefault();
                    onSelect(s.album_index);
                  }
                }}
                aria-label={`Album ${title}, ${count > 0 ? `${count} photos` : 'aucune photo pour le moment'}`}
                className="group text-left rounded-3xl border bg-white shadow-sm overflow-hidden transition-all duration-150 active:scale-[0.98] hover:shadow-md cursor-pointer"
                style={{ borderColor: BRAND.border, minHeight: 44 }}
              >
                {/* Cover thumbnail — fixed aspect ratio reserves space so the
                    grid doesn't reflow while images load (no layout shift). */}
                <div className="relative w-full" style={{ aspectRatio: '3 / 2', background: s.cover?.dominant_color ?? BRAND.creamSoft }}>
                  {s.cover ? (
                    /* FULL composited cover (bg + design icon + title text),
                       same as the cover-selection page via <CoverThumbnail>.
                       The cover is portrait (3:4); we center it at full height
                       inside the existing 3:2 card area (layout unchanged), and
                       the card bg = the cover's color so the margins blend. */
                    <div className="absolute inset-0 flex items-center justify-center">
                      <CoverThumbnail
                        cover={s.cover as Cover}
                        destinationText={s.destination ?? ''}
                        yearText={s.year ?? ''}
                        interactive={false}
                        className="h-full"
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: BRAND.creamSoft }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={BRAND.gold} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span className="text-[11px] font-semibold mt-1" style={{ color: BRAND.muted }}>Couverture à venir</span>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm" style={{ background: 'rgba(255,255,255,0.92)', color: BRAND.goldDeep }}>
                    Album {s.album_index}
                  </div>
                </div>

                <div className="p-4">
                  {editingSessionId === s.id ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <AlbumTitleInput
                        initial={s.destination ?? ''}
                        onCommit={(value) => onSaveTitle(s.id, value)}
                        onCancel={onCancelEdit}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-0.5 min-w-0">
                      <div className="text-base font-bold leading-tight truncate" style={{ color: BRAND.ink, fontFamily: 'Georgia, serif' }}>
                        {title}
                      </div>
                      <PencilButton onClick={(e) => { e.stopPropagation(); onStartEdit(s.id); }} />
                    </div>
                  )}
                  {titleError?.sessionId === s.id && (
                    <div className="text-xs mt-1" style={{ color: '#DC2626' }}>{titleError.message}</div>
                  )}
                  <div className="text-sm mt-1 font-medium" style={{ color: count > 0 ? BRAND.goldDeep : BRAND.muted }}>
                    {count === 0 && <span aria-hidden="true">📷 </span>}{countLabel}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Album title inline edit (shared: upload header + selection cards) ──────

/** Small ✎ button that opens the inline title editor. Muted, gold on hover. */
function PencilButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      type="button"
      aria-label="Renommer l'album"
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-lg shrink-0 transition-colors"
      style={{ minWidth: 32, minHeight: 32, color: BRAND.muted }}
      onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.goldDeep; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.muted; }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
      </svg>
    </button>
  );
}

/** Inline title input + ✓ confirm. Enter / ✓ / blur commit; Escape cancels;
 *  an empty value cancels (no save). `doneRef` guards the double-fire when
 *  blur follows an Enter/Escape/✓ that already resolved the editor. */
function AlbumTitleInput({ initial, onCommit, onCancel }: {
  initial: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef(false);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    const value = draft.trim().slice(0, 60);
    if (!value || value === initial.trim()) { onCancel(); return; }
    onCommit(value);
  };
  const cancel = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onCancel();
  };

  return (
    <span className="inline-flex items-center gap-1.5 min-w-0" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={draft}
        maxLength={60}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // stopPropagation: the selection card is a role="button" — Enter/
          // Space there must not re-select the album while typing.
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        onBlur={commit}
        aria-label="Titre de l'album"
        placeholder="Titre de l'album"
        className="rounded-lg border bg-white px-2.5 py-1.5 text-sm font-semibold outline-none min-w-0 w-44 sm:w-56"
        style={{ borderColor: BRAND.border, color: BRAND.ink }}
        onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${BRAND.gold}`; }}
        onBlurCapture={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      />
      <button
        type="button"
        aria-label="Confirmer le titre"
        // preventDefault so the input doesn't blur-commit before this click.
        onMouseDown={(e) => e.preventDefault()}
        onClick={commit}
        className="inline-flex items-center justify-center rounded-lg shrink-0 transition active:scale-[0.95]"
        style={{ minWidth: 32, minHeight: 32, color: '#fff', background: BRAND.gold }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </button>
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: BRAND.muted }}>{label}</div>
      <div className="text-sm font-semibold" style={{ color: BRAND.ink }}>{value}</div>
    </div>
  );
}

function ShellCenter({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: BRAND.cream }}>
      {children}
    </div>
  );
}

function Spinner({ small }: { small?: boolean } = {}) {
  const size = small ? 'w-5 h-5 border-2' : 'w-8 h-8 border-2';
  return <div className={`${size} rounded-full animate-spin`} style={{ borderColor: BRAND.gold, borderTopColor: 'transparent' }} />;
}

// ─── Confetti ───────────────────────────────────────────────────────────────
function fireConfetti() {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { document.body.removeChild(canvas); return; }
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  canvas.style.width  = window.innerWidth + 'px';
  canvas.style.height = window.innerHeight + 'px';
  ctx.scale(dpr, dpr);
  const COLORS = ['#C9A24A', '#A8842F', '#F6F0E2', '#FFFFFF', '#E7C667', '#7A5A1F'];
  const W = window.innerWidth, H = window.innerHeight;
  type Bit = { x: number; y: number; vx: number; vy: number; rot: number; vr: number; size: number; color: string };
  const bits: Bit[] = Array.from({ length: 140 }, () => ({
    x: W * 0.5 + (Math.random() - 0.5) * 200,
    y: H * 0.35,
    vx: (Math.random() - 0.5) * 12,
    vy: -8 - Math.random() * 8,
    rot: Math.random() * Math.PI,
    vr: (Math.random() - 0.5) * 0.4,
    size: 6 + Math.random() * 6,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
  }));
  const start = performance.now();
  function frame(t: number) {
    const elapsed = t - start;
    ctx!.clearRect(0, 0, W, H);
    bits.forEach((b) => {
      b.vy += 0.35;
      b.vx *= 0.995;
      b.x += b.vx;
      b.y += b.vy;
      b.rot += b.vr;
      ctx!.save();
      ctx!.translate(b.x, b.y);
      ctx!.rotate(b.rot);
      ctx!.fillStyle = b.color;
      ctx!.fillRect(-b.size / 2, -b.size / 2, b.size, b.size * 0.45);
      ctx!.restore();
    });
    if (elapsed < 2800) requestAnimationFrame(frame);
    else document.body.removeChild(canvas);
  }
  requestAnimationFrame(frame);
}
